/* Atlas v3 — "My Stocks": the WATCHLIST stop of the My Stocks cluster
   (WATCHLIST / MY AGENT / ACCOUNTS — DeskSwitcher eyebrow over real routes;
   siblings are AgentDesk.jsx and Accounts.jsx). The old Radar, restyled:
   hairline rows, remove-with-undo, sort control, insight card, empty state.
   The former HOLDINGS sub-tab moved to Accounts.jsx in the cluster split.

   Brings its own shell (TickerStrip + BottomNav) like Overview — NOT
   wrapped in the old AppLayout. No starfield (Overview-only).

   Handoff override applied: hairline rows with ONE sentiment label
   (rating vocab) + sparkline + directional price/%. The mockup's
   sector-hue dots are DROPPED per the handoff spec ("no colored dots").

   Data wiring:
     • watchlist tickers   ← useWatchlists() (Clerk-backed; replaces the
       mockup's localStorage atlas_watch). remove-with-undo calls the
       hook's removeTicker + a local undo buffer.
     • row data + sentiment ← useWatchlistMeta() (name/sector/verdict/
       change1d/price) joined with useLivePrices for live price/%.
     • sentiment label      ← thesis_status direction (Bullish/Bearish/Neutral). */
import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { useUser } from '@clerk/clerk-react'
import {
  TickerCtx, useTicker, intent, Spark,
} from './lib'
import { TickerStrip, BottomNav, SearchPill, DeskSwitcher, MY_STOCKS_DESKS } from './shell'
import { DotGlobe } from './DotGlobe'
import { WatchDeck } from './WatchDeck'
import { StockLogo } from '@/v2/components/StockLogo'
import { useSparks, useStockUpdates } from '@/v2/hooks/useSupabaseData'
import { StockDetailProvider, useStockDetail } from '@/v2/lib/StockDetailProvider'
import { useWatchlists } from '@/v2/hooks/useWatchlists'
import { useWatchlistMeta } from '@/v2/hooks/useWatchlistMeta'
import { useAtlasStocks } from '@/v2/hooks/useAtlasStocks'
import { useLivePrices } from '@/v2/hooks/useLivePrices'
import { useTickerBars } from '@/v2/hooks/useChartBars'

const Mono = { fontFamily: 'var(--studio-font-mono)' }
const Display = { fontFamily: 'var(--studio-font-display)', fontStyle: 'italic' }
const Sans = { fontFamily: 'var(--studio-font-sans)' }

/* ── sentiment label — one concise direction word per row, colored by it ──
   Real directional sentiment from thesis_status (BULLISH/BEARISH/NEUTRAL).
   The prescriptive rating tier (Top Conviction / Tracking / …) was dropped
   to match the ticker popout — no buy/sell verdict vocab on the rows. */
function sentimentFor(meta) {
  const v = meta?.verdict
  if (v === 'BULLISH') return { label: 'Bullish', t: '--studio-success' }
  if (v === 'BEARISH') return { label: 'Bearish', t: '--studio-danger' }
  if (v === 'NEUTRAL') return { label: 'Neutral', t: '--studio-text-subtle' }
  return null
}

/* ── price/% column ── */
function PriceCol({ price, pct, valueLabel, minWidth = 62 }) {
  const up = (pct ?? 0) >= 0
  return (
    <div style={{ textAlign: 'right', flexShrink: 0, minWidth }}>
      <div style={{ ...Mono, fontSize: 13, color: 'hsl(var(--studio-text))', fontVariantNumeric: 'tabular-nums' }}>
        {valueLabel != null ? valueLabel : (price > 0 ? `$${price.toFixed(2)}` : '—')}
      </div>
      <div style={{ ...Mono, fontSize: 11, marginTop: 3, fontVariantNumeric: 'tabular-nums', color: pct != null ? `hsl(var(${intent(up)}))` : 'hsl(var(--studio-text-subtle))' }}>
        {pct != null ? `${up ? '+' : ''}${pct.toFixed(1)}%` : '—'}
      </div>
    </div>
  )
}

/* ── watchlist row — hairline; swipe left to reveal Remove ── */
function WRow({ r, onOpen, onRemove }) {
  const [hot, setHot] = useState(false)
  const [offset, setOffset] = useState(0)
  const baseRef = useRef(0), startRef = useRef(null), movedRef = useRef(false), lockRef = useRef(null)
  const up = (r.pct ?? 0) >= 0
  const sent = sentimentFor(r)
  const clamp = (v) => Math.max(-86, Math.min(0, v))

  function onDown(e) {
    const t = e.touches?.[0] ?? e
    startRef.current = { x: t.clientX, y: t.clientY }
    baseRef.current = offset; movedRef.current = false; lockRef.current = null; setHot(true)
  }
  function onMove(e) {
    if (!startRef.current) return
    const t = e.touches?.[0] ?? e
    const dx = t.clientX - startRef.current.x, dy = t.clientY - startRef.current.y
    if (lockRef.current === null) {
      if (Math.abs(dx) > 6 || Math.abs(dy) > 6) lockRef.current = Math.abs(dx) >= Math.abs(dy) ? 'x' : 'y'
    }
    if (lockRef.current !== 'x') return
    movedRef.current = true; setOffset(clamp(baseRef.current + dx))
  }
  function onUp() {
    setHot(false)
    if (!startRef.current) return
    const moved = movedRef.current, lock = lockRef.current
    startRef.current = null
    if (lock === 'x') { setOffset((prev) => (prev < -44 ? -86 : 0)); return }
    if (!moved) { if (offset !== 0) setOffset(0); else onOpen() }
  }

  return (
    <div style={{ position: 'relative', overflow: 'hidden', borderBottom: '1px solid hsl(var(--studio-border) / 0.35)' }}>
      <button onClick={() => onRemove(r.ticker)} aria-label={`Remove ${r.ticker}`}
        style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: 86, border: 'none', cursor: 'pointer',
          background: 'hsl(var(--studio-danger))', color: 'hsl(var(--studio-background))', ...Mono, fontSize: 10, letterSpacing: '0.08em' }}>REMOVE</button>
      <div onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}
        style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 12, padding: '12px 20px', cursor: 'pointer',
          background: hot ? 'hsl(var(--studio-surface-2))' : 'hsl(var(--studio-background))',
          transform: `translateX(${offset}px)`,
          transition: startRef.current ? 'none' : 'transform 220ms var(--studio-ease), background 140ms', touchAction: 'pan-y' }}>
        <StockLogo ticker={r.ticker} size={30} rounded={15} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 3 }}>
            <span style={{ ...Mono, fontSize: 13.5, letterSpacing: '0.01em', transition: 'color 160ms',
              color: hot ? 'hsl(var(--studio-accent-soft))' : 'hsl(var(--studio-text))' }}>{r.ticker}</span>
            {sent && (
              <span style={{ ...Mono, fontSize: 9.5, letterSpacing: '0.04em', color: `hsl(var(${sent.t}))` }}>{sent.label}</span>
            )}
          </div>
          <div style={{ ...Sans, fontSize: 11, color: 'hsl(var(--studio-text-subtle))', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 150 }}>{r.name}</div>
        </div>
        <Spark data={r.spark} up={up} />
        <PriceCol price={r.price} pct={r.pct} />
      </div>
    </div>
  )
}

/* ── empty state — the globe above, the WatchDeck below. One composed
   screen: swipe right to star, left to skip, tap a card for the sheet. ── */
function EmptyState({ deck }) {
  return (
    <div style={{ padding: '14px 0 30px', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '0 20px' }}>
        <DotGlobe size={110} style={{ marginBottom: 12 }} />
        <div style={{ ...Display, fontSize: 24, color: 'hsl(var(--studio-text))', lineHeight: 1.1 }}>Nothing on your radar</div>
        <div style={{ ...Mono, fontSize: 8.5, letterSpacing: '0.1em', color: 'hsl(var(--studio-text-subtle))', margin: '9px 0 16px' }}>
          SWIPE RIGHT TO WATCH · LEFT TO SKIP · TAP FOR THE SHEET
        </div>
      </div>
      <WatchDeck {...deck} />
    </div>
  )
}

/* ── MORE COMPANIES sheet — the same deck, dealt over the filled list
   (MoreSheet shell: backdrop, grabber, amber hairline, slide-up). ── */
function DeckSheet({ deck, onClose }) {
  const [shown, setShown] = useState(false)
  useEffect(() => { const a = requestAnimationFrame(() => setShown(true)); return () => cancelAnimationFrame(a) }, [])
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 64 }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'hsl(var(--studio-overlay) / 0.66)', backdropFilter: 'blur(3px)', WebkitBackdropFilter: 'blur(3px)', opacity: shown ? 1 : 0, transition: 'opacity 220ms ease' }} />
      <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, maxHeight: '88%', display: 'flex', flexDirection: 'column',
        background: 'hsl(var(--studio-background))', borderTop: '2px solid hsl(var(--studio-accent) / 0.5)', borderRadius: '16px 16px 0 0',
        boxShadow: '0 -16px 44px hsl(var(--studio-overlay) / 0.6)',
        transform: shown ? 'translateY(0)' : 'translateY(100%)', transition: 'transform 340ms var(--studio-ease)', overflow: 'hidden' }}>
        <div style={{ padding: '9px 20px 0', flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
            <span onClick={onClose} style={{ width: 38, height: 4, borderRadius: 999, background: 'hsl(var(--studio-border))', cursor: 'pointer' }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ ...Display, fontSize: 24, color: 'hsl(var(--studio-text))' }}>More companies</span>
            <button onClick={onClose} aria-label="Close" style={{ width: 30, height: 30, borderRadius: 'var(--studio-radius-sm)', cursor: 'pointer', background: 'hsl(var(--studio-surface-2))', border: '1px solid hsl(var(--studio-border))', color: 'hsl(var(--studio-text-muted))', fontSize: 14 }}>✕</button>
          </div>
          <div style={{ ...Mono, fontSize: 8.5, letterSpacing: '0.1em', color: 'hsl(var(--studio-text-subtle))', marginBottom: 14 }}>
            SWIPE RIGHT TO WATCH · LEFT TO SKIP · TAP FOR THE SHEET
          </div>
        </div>
        <div className="atlas-scroll" style={{ flex: 1, overflowY: 'auto', paddingBottom: 30 }}>
          <WatchDeck {...deck} />
        </div>
      </div>
    </div>
  )
}

function WatchSkeleton() {
  return (
    <div style={{ marginTop: 8 }}>
      <div className="atlas-skeleton" style={{ width: 200, height: 52, borderRadius: 10, margin: '0 20px 14px' }} />
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 20px', borderBottom: '1px solid hsl(var(--studio-border) / 0.35)' }}>
          <div className="atlas-skeleton" style={{ width: 30, height: 30, borderRadius: '50%' }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="atlas-skeleton" style={{ width: `${40 + (i % 3) * 14}%`, height: 13, borderRadius: 3 }} />
            <div className="atlas-skeleton" style={{ width: '62%', height: 9, borderRadius: 3, marginTop: 7 }} />
          </div>
          <div className="atlas-skeleton" style={{ width: 52, height: 22, borderRadius: 4 }} />
        </div>
      ))}
    </div>
  )
}

/* ── Atlas-voice insight from the radar rows ── */
function buildInsight(rows) {
  if (!rows.length) return null
  const gainers = rows.filter((r) => (r.pct ?? 0) > 0.5)
  const losers = rows.filter((r) => (r.pct ?? 0) < -0.5)
  const parts = []
  if (gainers.length) {
    const top = [...gainers].sort((a, b) => b.pct - a.pct)[0]
    // The summary line above already says "<TICKER> LEADS" and the row below
    // shows the same %, so leading with both made it the third telling of one
    // fact. Keep the ticker as the subject and the one-liner, which is the only
    // thing here the screen doesn't otherwise say.
    const note = top.one_liner ? `${top.one_liner.replace(/\.*$/, '')}.` : `Atlas reads it ${(sentimentFor(top)?.label || 'in play').toLowerCase()}.`
    parts.push(`${top.ticker} — ${note.charAt(0).toLowerCase()}${note.slice(1)}`)
  }
  if (losers.length) {
    const bot = [...losers].sort((a, b) => a.pct - b.pct)[0]
    if (!gainers.length || bot.ticker !== [...gainers].sort((a, b) => b.pct - a.pct)[0].ticker) {
      parts.push(`${bot.ticker} under pressure at ${bot.pct.toFixed(1)}%. Atlas is watching closely.`)
    }
  }
  if (!parts.length) {
    return rows.length === 1
      ? `${rows[0].ticker} is on your radar — no strong move today.`
      : `${rows.length} names on your radar. Quiet session — no outsized moves yet.`
  }
  return parts.join(' ')
}

const SORTS = [['move', 'Move'], ['pct', '%'], ['az', 'A–Z']]

/* ── Watchlist sub-tab ── */
function WatchlistPanel({ openTicker }) {
  const { watchlists, removeTicker, addTicker, create, loading: wlLoading } = useWatchlists()
  const metaFor = useWatchlistMeta()
  // Deck fuel — the active universe rows already carry one_liner/what_they_do/
  // cron_summary/thesis_status from atlas-data; sparks are the shared 5D map;
  // the dealer's health gate reads the nightly 1Y column (latest row/ticker).
  const { stocks: universe } = useAtlasStocks()
  const { sparks } = useSparks()
  const { updates } = useStockUpdates()
  const perf1yMap = useMemo(() => {
    const m = new Map()
    for (const u of (updates || [])) {
      const cur = m.get(u.ticker)
      if (!cur || (u.updated_at && (!cur.updated_at || u.updated_at > cur.updated_at))) m.set(u.ticker, u)
    }
    return m
  }, [updates])
  const perf1y = useCallback((t) => {
    const v = perf1yMap.get(t)?.price_1y_change
    return typeof v === 'number' ? v : null
  }, [perf1yMap])

  // union of all the user's lists (deduped) — the merged radar
  const tickers = useMemo(() => {
    const set = new Set()
    for (const w of watchlists) for (const t of w.tickers) set.add(t)
    return [...set]
  }, [watchlists])

  // live prices fill in price/% where stock_updates lags
  const { prices } = useLivePrices(tickers)
  // A4 — real per-ticker close series for the row sparklines (small set;
  // useTickerBars batches + caps). 1D to match the 1D % the row displays
  // (was hardcoded '5D', which never matched the shown %). Falls back to a
  // neutral flat line.
  const { barsByTicker } = useTickerBars(tickers, '1D')

  // Settings "Default sort" (default_sort pref) seeds the initial sort; the
  // in-page chips still change it freely for the session.
  const [sort, setSort] = useState(() => {
    try {
      const s = JSON.parse(localStorage.getItem('atlas.pref.default_sort'))
      if (s === 'move' || s === 'pct' || s === 'az') return s
    } catch { /* default */ }
    return 'move'
  })
  const [undo, setUndo] = useState(null)

  const rows = useMemo(() => {
    const rs = tickers.map((t) => {
      const m = metaFor(t)
      const live = prices[t]
      // No invented zeros: a name missing from both /api/prices and
      // stock_updates keeps pct=null and renders "—" (seam ≠ fallback).
      const pct = live?.change1d != null ? live.change1d : (m.change1d ?? null)
      const price = live?.price != null ? live.price : (m.price ?? 0)
      return {
        ticker: t,
        name: m.name,
        sector: m.sector,
        one_liner: m.one_liner,
        verdict: m.verdict,
        rating: m.rating,
        price,
        pct,
        spark: barsByTicker[t] ?? null,
      }
    })
    // null pct sorts last in both numeric sorts
    if (sort === 'move') rs.sort((a, b) => (b.pct == null ? -1 : Math.abs(b.pct)) - (a.pct == null ? -1 : Math.abs(a.pct)))
    else if (sort === 'pct') rs.sort((a, b) => (b.pct ?? -Infinity) - (a.pct ?? -Infinity))
    else rs.sort((a, b) => a.ticker.localeCompare(b.ticker))
    return rs
  }, [tickers, metaFor, prices, barsByTicker, sort])

  const insight = useMemo(() => buildInsight(rows), [rows])
  // Radar-beat average over names that actually have a number — null when none do
  const livePcts = rows.map((r) => r.pct).filter((v) => v != null)
  const avg = livePcts.length ? livePcts.reduce((a, v) => a + v, 0) / livePcts.length : null
  const leader = rows.length ? [...rows].sort((a, b) => (b.pct ?? -Infinity) - (a.pct ?? -Infinity))[0] : null

  // remove-with-undo: snapshot which lists held the ticker so undo can restore
  const removeTickerWithUndo = useCallback((t) => {
    const fromLists = watchlists.filter((w) => w.tickers.includes(t)).map((w) => w.id)
    fromLists.forEach((id) => removeTicker(id, t))
    setUndo({ t, fromLists })
    setTimeout(() => setUndo((u) => (u && u.t === t ? null : u)), 4200)
  }, [watchlists, removeTicker])

  const undoRemove = useCallback(() => {
    if (!undo) return
    undo.fromLists.forEach((id) => addTicker(id, undo.t))
    setUndo(null)
  }, [undo, addTicker])

  // ── WatchDeck wiring — star from the deck into the user's first REAL list.
  // A brand-new signed-in user has no list yet: create() is optimistic with a
  // temp id, so buffer the stars and flush once the real id lands. Signed-out
  // stars stay session-local (same behavior as the Wire's ★) with an honest
  // toast. localStars keeps starred cards out of the deck either way.
  const [toast, setToast] = useState(null)
  const [deckOpen, setDeckOpen] = useState(false)
  const [localStars, setLocalStars] = useState(() => new Set())
  const pendingRef = useRef([])
  const signedIn = !!useUser().user

  const onDeckStar = useCallback((t) => {
    const tk = String(t).toUpperCase()
    setLocalStars((prev) => new Set(prev).add(tk))
    const real = watchlists.find((w) => !String(w.id).startsWith('tmp_'))
    if (real) addTicker(real.id, tk)
    else if (signedIn) {
      pendingRef.current.push(tk)
      if (watchlists.length === 0) create('My Radar')
    }
    const msg = signedIn ? `${tk} added to Radar` : `${tk} starred — sign in to save it`
    setToast(msg)
    setTimeout(() => setToast((x) => (x === msg ? null : x)), 1700)
  }, [watchlists, addTicker, create, signedIn])

  useEffect(() => {
    if (!pendingRef.current.length) return
    const real = watchlists.find((w) => !String(w.id).startsWith('tmp_'))
    if (real) { pendingRef.current.forEach((t) => addTicker(real.id, t)); pendingRef.current = [] }
  }, [watchlists, addTicker])

  const watched = useMemo(() => new Set([...tickers, ...localStars]), [tickers, localStars])
  const deck = { stocks: universe, sparks, watched, onStar: onDeckStar, onOpen: openTicker, perf1y }

  const toastEl = toast && (
    <div style={{ position: 'fixed', left: 16, right: 16, bottom: 'calc(96px + env(safe-area-inset-bottom))', zIndex: 80,
      display: 'flex', justifyContent: 'center', pointerEvents: 'none' }}>
      <span style={{ ...Sans, fontSize: 12.5, color: 'hsl(var(--studio-text))', padding: '10px 18px',
        background: 'hsl(var(--studio-surface-2))', border: '1px solid hsl(var(--studio-border))', borderRadius: 999,
        boxShadow: '0 12px 30px hsl(var(--studio-overlay) / 0.5)' }}>{toast}</span>
    </div>
  )

  if (wlLoading && watchlists.length === 0) return <WatchSkeleton />
  if (rows.length === 0) return <>{toastEl}<EmptyState deck={deck} /></>

  return (
    <div style={{ paddingBottom: 22 }}>
      {/* radar beat */}
      <div style={{ ...Mono, fontSize: 9.5, letterSpacing: '0.08em', color: 'hsl(var(--studio-text-muted))', padding: '4px 20px 0' }}>
        {rows.length} NAMES{avg != null && <> · <span style={{ color: `hsl(var(${intent(avg >= 0)}))` }}>{avg >= 0 ? '+' : ''}{avg.toFixed(1)}% TODAY</span></>}
        {leader && <span> · {leader.ticker} LEADS</span>}
      </div>

      {/* insight card — restyled (no left stripe) */}
      {insight && (
        <div style={{ margin: '12px 20px 2px', padding: '14px 16px', borderRadius: 8,
          background: 'hsl(var(--studio-surface))', border: '1px solid hsl(var(--studio-border) / 0.7)' }}>
          <div style={{ ...Mono, fontSize: 8.5, letterSpacing: '0.18em', color: 'hsl(var(--studio-accent))', marginBottom: 8 }}>TODAY ON YOUR RADAR</div>
          <p style={{ ...Sans, fontSize: 12.5, lineHeight: 1.65, color: 'hsl(var(--studio-text-muted))', margin: 0, textWrap: 'pretty' }}>{insight}</p>
        </div>
      )}

      {/* sort control */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px 9px' }}>
        <span style={{ ...Mono, fontSize: 8.5, letterSpacing: '0.14em', color: 'hsl(var(--studio-text-subtle))' }}>SORT</span>
        <div style={{ display: 'flex', gap: 5 }}>
          {SORTS.map(([id, label]) => {
            const on = sort === id
            return (
              <button key={id} onClick={() => setSort(id)} style={{ ...Mono, fontSize: 9.5, letterSpacing: '0.06em', padding: '4px 11px', borderRadius: 999, cursor: 'pointer',
                background: on ? 'hsl(var(--studio-surface-2))' : 'transparent', color: on ? 'hsl(var(--studio-text))' : 'hsl(var(--studio-text-subtle))',
                border: `1px solid ${on ? 'hsl(var(--studio-border))' : 'hsl(var(--studio-border) / 0.5)'}` }}>{label}</button>
            )
          })}
        </div>
      </div>

      <div style={{ borderTop: '1px solid hsl(var(--studio-border) / 0.5)' }}>
        {rows.map((r) => <WRow key={r.ticker} r={r} onOpen={() => openTicker(r.ticker)} onRemove={removeTickerWithUndo} />)}
      </div>
      <div style={{ padding: '14px 20px 8px', ...Mono, fontSize: 9, letterSpacing: '0.04em', color: 'hsl(var(--studio-text-subtle))', lineHeight: 1.6 }}>
        Swipe a row left to remove · ★ any ticker to add it here.
      </div>

      {/* deal the deck over the filled list — discovery stays a feature */}
      <button onClick={() => setDeckOpen(true)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%',
        padding: '15px 20px', cursor: 'pointer', background: 'transparent', border: 'none', borderTop: '1px solid hsl(var(--studio-border) / 0.5)' }}>
        <span style={{ ...Mono, fontSize: 10, letterSpacing: '0.14em', color: 'hsl(var(--studio-accent))' }}>MORE COMPANIES</span>
        <span style={{ color: 'hsl(var(--studio-accent))', fontSize: 13 }}>→</span>
      </button>
      {deckOpen && <DeckSheet deck={deck} onClose={() => setDeckOpen(false)} />}
      {toastEl}

      {/* undo snackbar */}
      {undo && (
        <div onClick={undoRemove} style={{ position: 'absolute', left: 16, right: 16, bottom: 16, zIndex: 40, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px',
          background: 'hsl(var(--studio-surface-2))', border: '1px solid hsl(var(--studio-border))', borderRadius: 'var(--studio-radius)',
          boxShadow: '0 12px 30px hsl(var(--studio-overlay) / 0.5)' }}>
          <span style={{ ...Sans, fontSize: 12.5, color: 'hsl(var(--studio-text))' }}>Removed {undo.t}</span>
          <span style={{ ...Mono, fontSize: 10, letterSpacing: '0.1em', color: 'hsl(var(--studio-accent-soft))' }}>UNDO</span>
        </div>
      )}
    </div>
  )
}

/* ── shell — the WATCHLIST stop; cluster nav via the DeskSwitcher eyebrow ── */
function MyStocksBody() {
  const { openTicker } = useStockDetail()

  return (
    <TickerCtx.Provider value={openTicker}>
      <div style={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column',
        background: 'radial-gradient(140% 75% at 50% -10%, hsl(var(--studio-background)) 0%, hsl(var(--studio-overlay)) 100%)' }}>
        <div style={{ flexShrink: 0 }}>
          <div style={{ padding: '18px 20px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ ...Display, fontSize: 30, color: 'hsl(var(--studio-text))', lineHeight: 1 }}>My Stocks</div>
            <SearchPill />
          </div>
          <DeskSwitcher items={MY_STOCKS_DESKS} variant="tabs" />
        </div>
        <div className="atlas-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', marginTop: 14, position: 'relative' }}>
          <WatchlistPanel openTicker={openTicker} />
        </div>
        <div style={{ flexShrink: 0, borderTop: '1px solid hsl(var(--studio-border) / 0.5)', background: 'hsl(var(--studio-background) / 0.72)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' }}>
          <TickerStrip />
        </div>
        <BottomNav />
      </div>
    </TickerCtx.Provider>
  )
}

export function MyStocks() {
  return (
    <div className="v3-root" style={{ height: 'var(--v3-vh, 100dvh)', display: 'flex', flexDirection: 'column' }}>
      <StockDetailProvider>
        <MyStocksBody />
      </StockDetailProvider>
    </div>
  )
}
